/**
 * @file fetchMetricIndex.ts
 * @description
 * Lightweight helper to load the prebuilt metric index (generated at build time)
 * so the client can render selectors without downloading every metric file.
 *
 * The index is expected at:
 *   /data/congress{congressId}/metrics/index.json
 *
 * Example entry:
 *   {
 *     "id": "veteran_count",
 *     "file": "data/congress118/metrics/veteran_count.json",
 *     "title": "Veteran Count"
 *     "congress": 118
 *   }
 *
 * This module:
 *  - Builds a reliable URL from options (congressId, basePath)
 *  - Fetches the index with optional memoization
 *  - Validates and normalizes the payload (dedupes + sorts by id)
 *  - Returns strongly-typed data or throws a descriptive error
 *
 * Pairs with a build-time generator that writes `index.json`
 * (e.g., via a small Node script or Vite plugin).
 */

import type { MetricIndex, MetricIndexEntry } from '@/types';
import { asset } from '@/utils/asset';

/** Options for fetching the metric index. */
export type FetchMetricIndexOptions = {
  /**
   * Congress to load from. Used to build the index URL.
   * @default 118
   */
  congressId?: number;

  /**
   * Base path under /public where data lives.
   * Final URL becomes `${basePath}/congress${congressId}/metrics/index.json`
   * @default "/data"
   */
  basePath?: string;

  /**
   * Fetch cache mode. Use "no-cache" during dev to avoid stale lists.
   * @default "no-cache"
   */
  cache?: RequestCache;

  /**
   * Abort signal for cancellation (e.g., on route change).
   */
  signal?: AbortSignal;

  /**
   * Validate the JSON shape and dedupe/sort in-memory.
   * @default true
   */
  validate?: boolean;

  /**
   * Use an in-memory promise cache keyed by the index URL to avoid duplicate
   * concurrent fetches. Set false to always hit the network.
   * @default true
   */
  memoize?: boolean;
};

/** Internal memoization map keyed by the resolved index URL. */
const _memo = new Map<string, Promise<MetricIndex>>();

/**
 * Fetch the prebuilt metric index for a given Congress.
 *
 * @example
 * ```ts
 * import { fetchMetricIndex } from "@/domain/metrics/fetchMetricIndex";
 *
 * // Load once at app bootstrap:
 * const index = await fetchMetricIndex({ congressId: 118 });
 * // Populate a dropdown:
 * const options = index.map(e => ({ value: e.id, label: e.title }));
 * ```
 *
 * @param options - Controls URL construction, caching, validation, and aborting.
 * @returns A promise resolving to the normalized metric index.
 * @throws If the request fails or the payload is not an array of valid entries.
 */
export async function fetchMetricIndex(
  options: FetchMetricIndexOptions = {},
): Promise<MetricIndex> {
  const {
    congressId = 118,
    basePath = '/data',
    cache = 'no-cache',
    signal,
    validate = true,
    memoize = true,
  } = options;

  const url = buildIndexUrl(basePath, congressId);

  if (memoize) {
    const cached = _memo.get(url);
    if (cached) return cached;
  }

  const task = (async () => {
    const res = await fetch(url, { cache, signal });

    if (!res.ok) {
      throw new Error(`Failed to fetch metric index (${res.status} ${res.statusText}) at ${url}`);
    }

    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new Error(`Metric index at ${url} is not valid JSON.`);
    }

    if (!Array.isArray(json)) {
      throw new Error(`Metric index at ${url} must be a JSON array.`);
    }

    // Lightweight validation + normalization
    const entries = json.filter(isPlainObject);
    let typed: MetricIndex = entries.map(coerceToEntry);

    if (validate) {
      // Remove invalid, dedupe by id, and sort
      const byId = new Map<string, MetricIndexEntry>();
      for (const e of typed) {
        if (isValidEntry(e)) {
          if (!byId.has(e.id)) byId.set(e.id, e);
        }
      }
      typed = Array.from(byId.values()).sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }),
      );
    }

    return typed;
  })();

  if (memoize) _memo.set(url, task);
  try {
    const result = await task;
    return result;
  } catch (e) {
    // On failure, clear the memoized promise so a later call can retry.
    if (memoize) _memo.delete(url);
    throw e;
  }
}

/**
 * Build the canonical URL for the metric index JSON.
 * Ensures a single leading slash and no trailing slashes in the base path.
 *
 * @example
 * buildIndexUrl("/data", 118)
 * // => "/data/congress118/metrics/index.json"
 */
function buildIndexUrl(basePath: string, congressId: number): string {
  // Normalize basePath to a relative segment (no leading/trailing slashes)
  const root = (basePath ?? '/data').toString().replace(/^\/+|\/+$/g, '');
  // Build a relative asset path; `asset()` will prepend import.meta.env.BASE_URL
  const path = `${root}/congress${congressId}/metrics/index.json`;
  return asset(path);
}

/** Narrowing: is the value a non-null object (but not an array)? */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Coerce a loose object into a MetricIndexEntry, filling safe defaults. */
function coerceToEntry(obj: Record<string, unknown>): MetricIndexEntry {
  const idValue = obj.id;
  const fileValue = obj.file;
  let id = '';
  if (typeof idValue === 'string') {
    id = idValue;
  } else if (typeof idValue === 'number' || typeof idValue === 'boolean') {
    id = String(idValue);
  }
  id = id.trim();

  let file = '';
  if (typeof fileValue === 'string') {
    file = fileValue;
  } else if (typeof fileValue === 'number' || typeof fileValue === 'boolean') {
    file = String(fileValue);
  }
  file = file.trim();
  const titleRaw = obj.title;
  const congress = toNumber(obj.congress, 0);

  // Title fallback: prefer provided title, else use id, else file name
  const title =
    (typeof titleRaw === 'string' && titleRaw.trim()) || id || basename(file) || 'Untitled';

  return { id, file, title, congress };
}

/** Validate the final coerced entry. */
function isValidEntry(e: MetricIndexEntry): boolean {
  if (!e.id) return false;
  if (!e.file || !e.file.endsWith('.json')) return false;
  if (!Number.isFinite(e.congress) || e.congress <= 0) return false;
  return true;
}

/** Convert to number safely; returns fallback if NaN. */
function toNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Extract final path segment without extension. */
function basename(p: string): string {
  const last = p.split('/').filter(Boolean).pop() ?? '';
  return last.replace(/\.json$/i, '');
}
